import { Capacitor, registerPlugin } from '@capacitor/core';
import type { PluginListenerHandle } from '@capacitor/core';
import type { NativeMidiDeviceState } from 'Utils/MuseFlowMidiPlugin';

// MUS-450 - Bluetooth (BLE) MIDI keyboards on Android.
//
// Android never lists a Bluetooth MIDI keyboard by itself: the app has to open it. On
// Android 12+ the app searches for keyboards that advertise the Bluetooth MIDI service, so
// the keyboard need not be paired first; on older Android only paired keyboards are listed.
// The native side (MainActivity and MuseFlowBluetoothMidiPlugin) opens the keyboard and
// feeds it into the same MIDIReceived events as a USB keyboard, so notes, the connected
// state and scoring need nothing new here. This module only drives the connect steps:
// permission, the search, choosing a keyboard, connect and disconnect.

export const ANDROID_BLUETOOTH_MIDI_PLUGIN = 'MuseFlowBluetoothMidi';

export type BluetoothPermissionState = 'granted' | 'denied' | 'prompt' | 'prompt-with-rationale';

/** The keyboard the user connected last; the app retries it by itself after it goes away. */
export type SavedBluetoothKeyboard = {
  id: string;
  name: string;
  /** Unpaired in Android since it was saved, so the app no longer retries it. Absent in older builds. */
  unpaired?: boolean;
};

export type BluetoothMidiStatus = {
  supported: boolean;
  bluetoothOn: boolean;
  permission: BluetoothPermissionState;
  connectedName: string | null;
  /** Absent in app builds from before the MIDI prompt's Reconnect button. */
  savedKeyboard?: SavedBluetoothKeyboard | null;
  /** The app searches for keyboards itself (Android 12+). Absent in older app builds. */
  canSearch?: boolean;
};

/** A keyboard to choose: paired in Android, or found by the app's own search. */
export type PairedBluetoothKeyboard = {
  id: string;
  name: string;
  /** The device advertises the Bluetooth MIDI service. */
  midi: boolean;
  /** False for a keyboard the search found that is not paired. Absent in older app builds. */
  paired?: boolean;
  /** The signal strength the search last saw, in dBm, or null. */
  rssi?: number | null;
};

export type BluetoothKeyboardsChange = {
  /** The search the event belongs to. */
  searchId?: number;
  /** The whole list so far: paired keyboards first, then the ones found. */
  keyboards: PairedBluetoothKeyboard[];
};

export type BluetoothKeyboardSearchStopped = {
  searchId?: number;
  reason: string;
  /** Android refused or broke off the search. */
  failed?: boolean;
  /**
   * The search ran its full time and heard no device at all. Android drops a search started
   * too often without telling the app (it swallows the "scanning too frequently" error), but
   * a quiet room with the keyboard off sounds the same.
   */
  heardNothing?: boolean;
  /** After a failure or hearing nothing: how long until Android allows another search, or 0. */
  retryInMs?: number;
};

export type BluetoothMidiConnectionChange = {
  connected: boolean;
  name: string | null;
};

export interface AndroidBluetoothMidiPlugin {
  getStatus(): Promise<BluetoothMidiStatus>;
  requestPermission(): Promise<{ permission: BluetoothPermissionState }>;
  listPairedKeyboards(): Promise<{ keyboards: PairedBluetoothKeyboard[] }>;
  /** Starts a search of about 10 s; resolves at once with the paired keyboards. Android 12+. */
  searchKeyboards(): Promise<{ searchId?: number; keyboards: PairedBluetoothKeyboard[] }>;
  stopKeyboardSearch(): Promise<void>;
  connect(options: { id: string }): Promise<{ name: string }>;
  disconnect(): Promise<void>;
  openAppSettings(): Promise<void>;
  openBluetoothSettings(): Promise<void>;
  /** The keyboards connected now, USB and Bluetooth, as `MIDIDevicesChanged` carries them. */
  getMidiDeviceState(): Promise<NativeMidiDeviceState>;
  addListener(
    eventName: 'connectionChanged',
    handler: (change: BluetoothMidiConnectionChange) => void,
  ): Promise<PluginListenerHandle>;
  addListener(
    eventName: 'keyboardsChanged',
    handler: (change: BluetoothKeyboardsChange) => void,
  ): Promise<PluginListenerHandle>;
  addListener(
    eventName: 'keyboardSearchStopped',
    handler: (stopped: BluetoothKeyboardSearchStopped) => void,
  ): Promise<PluginListenerHandle>;
}

export const AndroidBluetoothMidi = registerPlugin<AndroidBluetoothMidiPlugin>(
  ANDROID_BLUETOOTH_MIDI_PLUGIN,
);

export type AndroidBluetoothMidiEnv = {
  isNative: boolean;
  platform: string;
  pluginAvailable: boolean;
};

/** Pure predicate: offer the Android Bluetooth keyboard connect steps. */
export const shouldOfferAndroidBluetoothMidi = (env: AndroidBluetoothMidiEnv): boolean =>
  env.isNative && env.platform === 'android' && env.pluginAvailable;

export const isAndroidBluetoothMidiAvailable = (): boolean =>
  shouldOfferAndroidBluetoothMidi({
    isNative: Capacitor.isNativePlatform(),
    platform: Capacitor.getPlatform(),
    pluginAvailable: Capacitor.isPluginAvailable(ANDROID_BLUETOOTH_MIDI_PLUGIN),
  });

export type BluetoothKeyboardStep =
  | { kind: 'idle' }
  | { kind: 'checking' }
  | { kind: 'unsupported' }
  | { kind: 'bluetooth-off' }
  | { kind: 'permission-denied' }
  /**
   * `heardNothing`: the search heard no device at all, so it may have been dropped.
   * `retryAt`: when Android allows the next search (ms since epoch), when the app knows.
   */
  | { kind: 'no-keyboards'; searched?: boolean; heardNothing?: boolean; retryAt?: number }
  /**
   * `retryAt`: when Android allows the next search (ms since epoch), when the app knows.
   * `keyboards`: the paired keyboards, offered while the user waits.
   */
  | { kind: 'search-failed'; retryAt?: number; keyboards?: PairedBluetoothKeyboard[] }
  | { kind: 'searching'; keyboards: PairedBluetoothKeyboard[] }
  | { kind: 'choose'; keyboards: PairedBluetoothKeyboard[]; searched?: boolean }
  | { kind: 'connecting'; name: string }
  | { kind: 'connected'; name: string }
  | { kind: 'failed'; name: string }
  | { kind: 'lost'; name: string }
  | { kind: 'error' };

/** Keyboards that advertise the MIDI service first; the native list is already sorted. */
export const stepForPairedKeyboards = (
  keyboards: PairedBluetoothKeyboard[],
): BluetoothKeyboardStep =>
  keyboards.length === 0
    ? { kind: 'no-keyboards' }
    : {
        kind: 'choose',
        keyboards: [
          ...keyboards.filter((keyboard) => keyboard.midi),
          ...keyboards.filter((keyboard) => !keyboard.midi),
        ],
      };

/**
 * From a tap on "Connect Bluetooth keyboard" to the list of paired keyboards. Asks for the
 * Bluetooth permission only when it is not granted yet; a refusal ends on
 * 'permission-denied', from which the user can try again or open the app's settings.
 */
export const findPairedBluetoothKeyboards = async (
  plugin: AndroidBluetoothMidiPlugin = AndroidBluetoothMidi,
): Promise<BluetoothKeyboardStep> => {
  try {
    const blocked = await stepBlockingAConnect(plugin);
    if (blocked) return blocked;

    const { keyboards } = await plugin.listPairedKeyboards();
    return stepForPairedKeyboards(keyboards);
  } catch {
    return { kind: 'error' };
  }
};

/**
 * What stops a connect right now (no support, Bluetooth off, the permission refused), or
 * null when the app may connect. Asks for the permission only when it is not granted yet.
 */
const stepBlockingAConnect = async (
  plugin: AndroidBluetoothMidiPlugin,
): Promise<BluetoothKeyboardStep | null> => {
  const status = await plugin.getStatus();
  if (!status.supported) return { kind: 'unsupported' };
  if (!status.bluetoothOn) return { kind: 'bluetooth-off' };

  const permission =
    status.permission === 'granted'
      ? status.permission
      : (await plugin.requestPermission()).permission;
  return permission === 'granted' ? null : { kind: 'permission-denied' };
};

/** The list while the search runs: as the native side sends it, paired keyboards first. */
export const stepWhileSearching = (
  keyboards: PairedBluetoothKeyboard[],
): BluetoothKeyboardStep => ({
  kind: 'searching',
  keyboards,
});

/**
 * The search failed; with a known wait, the step says when Search again works. Paired
 * keyboards, when given, are offered meanwhile.
 */
export const stepForFailedSearch = (
  retryInMs = 0,
  keyboards: PairedBluetoothKeyboard[] = [],
  now = Date.now(),
): BluetoothKeyboardStep => ({
  kind: 'search-failed',
  ...(retryInMs > 0 ? { retryAt: now + retryInMs } : {}),
  ...(keyboards.length > 0 ? { keyboards } : {}),
});

export type BluetoothKeyboardSearchOutcome = {
  failed?: boolean;
  heardNothing?: boolean;
  retryInMs?: number;
};

/**
 * When the search ends: choose from what it found, or say nothing was found. A search that
 * failed with nothing found says so, since "no keyboard found" would send the user to
 * check a keyboard that may be on and close. A search that heard nothing at all ran (a
 * wait known at its end means it was the last start the quota allowed, not one over it),
 * so the room was quiet or the search was dropped: it says no keyboard was found, and
 * Search again counts down a known wait or the copy mentions one.
 */
export const stepAfterSearch = (
  keyboards: PairedBluetoothKeyboard[],
  { failed = false, heardNothing = false, retryInMs = 0 }: BluetoothKeyboardSearchOutcome = {},
  now = Date.now(),
): BluetoothKeyboardStep => {
  if (keyboards.length > 0) return { kind: 'choose', keyboards, searched: true };
  if (failed) return stepForFailedSearch(retryInMs, [], now);
  if (!heardNothing) return { kind: 'no-keyboards', searched: true };
  return retryInMs > 0
    ? { kind: 'no-keyboards', searched: true, heardNothing: true, retryAt: now + retryInMs }
    : { kind: 'no-keyboards', searched: true, heardNothing: true };
};

/** A little longer than the native search, in case its end is never reported. */
export const KEYBOARD_SEARCH_SAFETY_MS = 12000;

export type BluetoothKeyboardSearch = {
  /** The Stop button: ends the search and offers what it found. */
  stop: () => void;
  /** Choosing a keyboard or leaving the page: ends the search and reports nothing more. */
  cancel: () => void;
};

const nativeErrorCode = (error: unknown): string | null => {
  if (typeof error !== 'object' || error === null || !('code' in error)) return null;
  const { code } = error as { code: unknown };
  return typeof code === 'string' ? code : null;
};

/** The wait a "too-frequent" refusal carries, or 0. */
const nativeRetryInMs = (error: unknown): number => {
  if (typeof error !== 'object' || error === null || !('data' in error)) return 0;
  const { data } = error as { data: unknown };
  if (typeof data !== 'object' || data === null || !('retryInMs' in data)) return 0;
  const { retryInMs } = data as { retryInMs: unknown };
  return typeof retryInMs === 'number' && retryInMs > 0 ? retryInMs : 0;
};

/**
 * The search is refused or not there (Android 11 and older, an app build from before the
 * search, no Bluetooth LE scanner): explain a refusal, otherwise offer the paired list.
 */
const stepWithoutSearch = async (
  error: unknown,
  plugin: AndroidBluetoothMidiPlugin,
): Promise<BluetoothKeyboardStep> => {
  const code = nativeErrorCode(error);
  if (code === 'permission') return { kind: 'permission-denied' };
  if (code === 'bluetooth-off') return { kind: 'bluetooth-off' };
  // Started again too soon: Android would drop it, so the app refused it and says how long,
  // and offers the paired keyboards meanwhile, as a search would have.
  if (code === 'too-frequent') {
    const keyboards = await plugin
      .listPairedKeyboards()
      .then((paired) => paired.keyboards)
      .catch(() => []);
    return stepForFailedSearch(nativeRetryInMs(error), keyboards);
  }
  try {
    const { keyboards } = await plugin.listPairedKeyboards();
    return stepForPairedKeyboards(keyboards);
  } catch {
    return { kind: 'error' };
  }
};

/**
 * From a tap on "Connect Bluetooth keyboard" to a running search: the same checks as
 * before (asking for Nearby devices only when it is not granted yet), then the search,
 * whose keyboards reach `onStep` as they are found, and its end. Resolves with the
 * running search, or null when it ended at once (a refusal, Bluetooth off, the paired-only
 * list on Android 11 and older).
 */
export const searchBluetoothKeyboards = async (
  onStep: (step: BluetoothKeyboardStep) => void,
  plugin: AndroidBluetoothMidiPlugin = AndroidBluetoothMidi,
): Promise<BluetoothKeyboardSearch | null> => {
  try {
    const blocked = await stepBlockingAConnect(plugin);
    if (blocked) {
      onStep(blocked);
      return null;
    }
  } catch {
    onStep({ kind: 'error' });
    return null;
  }

  let keyboards: PairedBluetoothKeyboard[] = [];
  let ended = false;
  // Events reach the page only after the search's start, so anything earlier, or with
  // another search's id, is the tail of a search already left behind.
  let started = false;
  let ownSearchId: number | undefined;
  const isOwnEvent = (eventSearchId: number | undefined) =>
    !ended && started && (eventSearchId === undefined || eventSearchId === ownSearchId);
  let safetyTimer: ReturnType<typeof setTimeout> | undefined;
  const listenerHandles: Promise<PluginListenerHandle>[] = [];

  const detach = () => {
    ended = true;
    if (safetyTimer !== undefined) clearTimeout(safetyTimer);
    listenerHandles.forEach((handle) => {
      void handle.then((listener) => listener.remove()).catch(() => undefined);
    });
  };
  const stopNativeSearch = () => {
    void Promise.resolve()
      .then(() => plugin.stopKeyboardSearch())
      .catch(() => undefined);
  };
  const finish = (outcome: BluetoothKeyboardSearchOutcome = {}) => {
    if (ended) return;
    detach();
    onStep(stepAfterSearch(keyboards, outcome));
  };
  const search: BluetoothKeyboardSearch = {
    stop: () => {
      stopNativeSearch();
      finish();
    },
    cancel: () => {
      if (ended) return;
      detach();
      stopNativeSearch();
    },
  };

  try {
    listenerHandles.push(
      plugin.addListener('keyboardsChanged', (change) => {
        if (!isOwnEvent(change.searchId)) return;
        keyboards = change.keyboards;
        onStep(stepWhileSearching(keyboards));
      }),
      plugin.addListener('keyboardSearchStopped', (stopped) => {
        if (!isOwnEvent(stopped.searchId)) return;
        finish({
          failed: stopped.failed === true,
          heardNothing: stopped.heardNothing === true,
          retryInMs: stopped.retryInMs ?? 0,
        });
      }),
    );
    const searchStart = await plugin.searchKeyboards();
    if (ended) return search;
    started = true;
    ownSearchId = searchStart.searchId;
    keyboards = searchStart.keyboards;
    onStep(stepWhileSearching(keyboards));
    safetyTimer = setTimeout(() => finish(), KEYBOARD_SEARCH_SAFETY_MS);
    return search;
  } catch (error) {
    detach();
    onStep(await stepWithoutSearch(error, plugin));
    return null;
  }
};

/**
 * The keyboard the user connected last that the app still reconnects, or null (none, an
 * older app build, or one the user unpaired in Android since: the app stopped retrying it,
 * so the prompt offers the connect steps instead of "reconnects when it is back on").
 */
export const savedBluetoothKeyboard = async (
  plugin: AndroidBluetoothMidiPlugin = AndroidBluetoothMidi,
): Promise<SavedBluetoothKeyboard | null> => {
  try {
    const { savedKeyboard } = await plugin.getStatus();
    return savedKeyboard?.id && savedKeyboard.unpaired !== true ? savedKeyboard : null;
  } catch {
    return null;
  }
};

/**
 * The MIDI prompt's "Reconnect <name>": the same checks as "Connect Bluetooth keyboard",
 * then a connect to the saved keyboard, with no list to choose from.
 */
export const reconnectSavedBluetoothKeyboard = async (
  keyboard: SavedBluetoothKeyboard,
  plugin: AndroidBluetoothMidiPlugin = AndroidBluetoothMidi,
): Promise<BluetoothKeyboardStep> => {
  try {
    const blocked = await stepBlockingAConnect(plugin);
    if (blocked) return blocked;
  } catch {
    return { kind: 'error' };
  }
  return connectPairedBluetoothKeyboard({ ...keyboard, midi: true }, plugin);
};

export const connectPairedBluetoothKeyboard = async (
  keyboard: PairedBluetoothKeyboard,
  plugin: AndroidBluetoothMidiPlugin = AndroidBluetoothMidi,
): Promise<BluetoothKeyboardStep> => {
  try {
    const { name } = await plugin.connect({ id: keyboard.id });
    return { kind: 'connected', name: name || keyboard.name };
  } catch {
    return { kind: 'failed', name: keyboard.name };
  }
};

/** The app searches for keyboards itself: Android 12+ in an app build that has the search. */
export const canSearchForBluetoothKeyboards = async (
  plugin: AndroidBluetoothMidiPlugin = AndroidBluetoothMidi,
): Promise<boolean> => {
  try {
    const { canSearch } = await plugin.getStatus();
    return canSearch === true;
  } catch {
    return false;
  }
};

/** The step to show when the settings page opens: connected if a keyboard already is. */
export const initialBluetoothKeyboardStep = async (
  plugin: AndroidBluetoothMidiPlugin = AndroidBluetoothMidi,
): Promise<BluetoothKeyboardStep> => {
  try {
    const status = await plugin.getStatus();
    return status.connectedName
      ? { kind: 'connected', name: status.connectedName }
      : { kind: 'idle' };
  } catch {
    return { kind: 'idle' };
  }
};

/** A native connection change while the page is open. */
export const stepAfterConnectionChange = (
  current: BluetoothKeyboardStep,
  change: BluetoothMidiConnectionChange,
): BluetoothKeyboardStep => {
  if (change.connected && change.name) return { kind: 'connected', name: change.name };
  if (current.kind === 'connected') return { kind: 'lost', name: current.name };
  return current;
};
